import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Video, Loader2, AlertTriangle } from 'lucide-react';
import { useVideoSession, useBranding } from '@utils/api';
import { Button } from '@/components/ui/button';
import {
  useMeeting,
  useLocalMedia,
  useVideoTiles,
  useDevices,
} from './VideoConference/hooks';
import {
  VideoTile,
  VideoGrid,
  ControlBar,
} from './VideoConference/primitives';
import { ErrorLanding } from './ErrorLanding';
import Loading from './Loading';

interface VideoContainerProps {
  sessionId: string;
  attendeeId: string;
}

// Internal component to reduce repeated card layout pattern
function StateCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-4 pt-24">
      <div className="bg-white rounded-lg p-6 shadow-md max-w-sm w-full text-center space-y-4">
        {children}
      </div>
    </div>
  );
}

export function VideoContainer({
  sessionId,
  attendeeId,
}: VideoContainerProps) {
  const { t, i18n } = useTranslation();
  const {
    videoSession,
    isLoading: isVideoSessionLoading,
    isError: isVideoSessionError,
  } = useVideoSession(sessionId, attendeeId);
  const { branding, isLoading: isBrandingLoading } = useBranding();

  // DOM-mounted audio element for iOS Safari autoplay policy compliance
  const audioRef = useRef<HTMLAudioElement>(null);
  const { meetingSession, meetingStatus, join, leave } = useMeeting({
    audioElementRef: audioRef,
  });

  // Derive call state from meeting status to avoid sync issues
  const callState = useMemo(() => {
    if (meetingStatus === 'connected') return 'active';
    if (meetingStatus === 'connecting') return 'joining';
    if (meetingStatus === 'reconnecting') return 'reconnecting';
    if (meetingStatus === 'disconnected') return 'ended';
    if (meetingStatus === 'failed') return 'failed';
    if (meetingStatus === 'permission-denied') return 'permission-denied';
    return 'idle';
  }, [meetingStatus]);

  // Get screen reader announcement for connection state changes
  const statusAnnouncement = useMemo(() => {
    switch (callState) {
      case 'joining':
        return t('videoSession.status.connecting', 'Connecting...');
      case 'active':
        return t('videoSession.status.connected', 'Connected to meeting');
      case 'reconnecting':
        return t('videoSession.status.reconnecting', 'Reconnecting...');
      case 'ended':
        return t('videoSession.status.ended', 'Meeting ended');
      default:
        return '';
    }
  }, [callState, t]);

  const { localTileId, remoteTiles, bindVideoElement } = useVideoTiles({
    meetingSession,
  });

  // useLocalMedia needs selectedCamera, useDevices needs isVideoOn
  // Initialize local media first with null camera, then get devices with video state
  const { isAudioOn, isVideoOn, toggleAudio, toggleVideo, selectedCameraRef } = useLocalMedia({
    meetingSession,
  });

  const {
    cameras,
    microphones,
    speakers,
    selectedCamera,
    selectedMic,
    selectedSpeaker,
    selectCamera,
    selectMic,
    selectSpeaker,
    supportsSpeakerSelection,
  } = useDevices({ meetingSession, isVideoOn });

  // Keep selectedCameraRef in sync for useLocalMedia
  selectedCameraRef.current = selectedCamera;

  // Set language preference from session
  useEffect(() => {
    if (videoSession?.languagePreference) {
      i18n.changeLanguage(videoSession.languagePreference);
    }
  }, [videoSession, i18n]);

  const handleJoinMeeting = async () => {
    if (!videoSession) return;
    try {
      await join(videoSession);
    } catch {
      // useMeeting already sets meetingStatus; no further action needed here
    }
  };

  const handleLeaveMeeting = async () => {
    await leave();
  };

  // Loading state
  if (isVideoSessionLoading || isBrandingLoading) {
    return <Loading />;
  }

  // Error state - session not found
  if (isVideoSessionError?.status === 404) {
    return (
      <ErrorLanding
        heading={t('videoSession.errors.notFound', 'Meeting not found')}
        subHeading={t('videoSession.errors.invalidLink', 'This link may have expired. If you think this is an error, please send your provider a message requesting a new link.')}
      />
    );
  }

  // Error state - other API errors (403, 500, network errors)
  if (isVideoSessionError) {
    return (
      <ErrorLanding
        heading={t('videoSession.errors.joinFailed', 'Unable to Join Meeting')}
        subHeading={t('videoSession.errors.joinFailedDescription', 'Something went wrong while connecting. Please try again.')}
      />
    );
  }

  return (
    <div
      className="h-svh w-screen overflow-hidden bg-background relative"
      style={{
        backgroundColor: branding?.backgroundColor,
      }}
    >
      {/* Branding header - hidden during idle state since logo is in the join card */}
      {callState !== 'idle' && (
        <header className="absolute top-0 left-0 right-0 z-10 p-2">
          <div className="max-w-xs mx-auto bg-white rounded-lg px-3 py-2 shadow-md">
            {branding?.logo && (
              <img
                src={branding.logo}
                alt={`${branding.name}'s Logo`}
                className="h-8 w-full object-contain"
              />
            )}
          </div>
        </header>
      )}

      {/* Pre-join state - combined card with logo and join button */}
      {callState === 'idle' && (
        <StateCard>
          {branding?.logo && (
            <img
              src={branding.logo}
              alt={`${branding.name}'s Logo`}
              className="h-16 w-full object-contain"
            />
          )}
          <Button
            onClick={handleJoinMeeting}
            size="lg"
            className="w-full"
          >
            {t('videoSession.actions.joinMeeting', 'Join Meeting')}
          </Button>
        </StateCard>
      )}

      {/* Joining state */}
      {callState === 'joining' && (
        <StateCard>
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">
            {t('videoSession.status.connecting', 'Connecting...')}
          </p>
        </StateCard>
      )}

      {/* Active call or reconnecting (show video area with optional overlay) */}
      {(callState === 'active' || callState === 'reconnecting') && (
        <>
          {/* Video area */}
          <div className="absolute inset-0 pt-14 pb-20">
            {remoteTiles.length > 0 ? (
              <VideoGrid>
                {remoteTiles.map((tile) => (
                  <VideoTile
                    key={tile.tileId}
                    tileId={tile.tileId}
                    bindVideoElement={bindVideoElement}
                    className="w-full h-full"
                  />
                ))}
              </VideoGrid>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Video className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg">
                  {t('videoSession.status.waitingForOthers', 'Waiting for others to join...')}
                </p>
              </div>
            )}
          </div>

          {/* Local video PiP - positioned above control bar */}
          {localTileId && isVideoOn && (
            <div className="absolute bottom-20 right-4 w-28 h-20 sm:w-36 sm:h-28 rounded-lg overflow-hidden shadow-lg border-2 border-white z-10">
              <VideoTile
                tileId={localTileId}
                bindVideoElement={bindVideoElement}
                isLocal
                className="w-full h-full"
              />
              <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                {t('videoSession.labels.you', 'You')}
              </div>
            </div>
          )}

          {/* Reconnecting overlay */}
          {callState === 'reconnecting' && (
            <div className="absolute inset-0 pt-14 pb-20 flex items-center justify-center bg-black/50 z-20">
              <div className="bg-white rounded-lg p-4 shadow-md flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground font-medium">
                  {t('videoSession.status.reconnecting', 'Reconnecting...')}
                </p>
              </div>
            </div>
          )}

          {/* Control bar */}
          <ControlBar
            isAudioOn={isAudioOn}
            isVideoOn={isVideoOn}
            onToggleAudio={toggleAudio}
            onToggleVideo={toggleVideo}
            onEndCall={handleLeaveMeeting}
            microphones={microphones}
            speakers={speakers}
            cameras={cameras}
            selectedMic={selectedMic}
            selectedSpeaker={selectedSpeaker}
            selectedCamera={selectedCamera}
            onSelectMic={selectMic}
            onSelectSpeaker={selectSpeaker}
            onSelectCamera={selectCamera}
            supportsSpeakerSelection={supportsSpeakerSelection}
            className="fixed bottom-0 left-0 right-0 pb-safe"
          />
        </>
      )}

      {/* Ended state */}
      {callState === 'ended' && (
        <StateCard>
          <h2 className="text-xl font-bold">
            {t('videoSession.titles.meetingEnded', 'Meeting Ended')}
          </h2>
          <p className="text-muted-foreground">
            {t('videoSession.titles.closeForPrivacy', 'You can close this window.')}
          </p>
        </StateCard>
      )}

      {/* Failed state */}
      {callState === 'failed' && (
        <StateCard>
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="text-xl font-bold">
            {t('videoSession.errors.joinFailed', 'Unable to Join Meeting')}
          </h2>
          <p className="text-muted-foreground">
            {t('videoSession.errors.joinFailedDescription', 'Something went wrong while connecting. Please try again.')}
          </p>
          <Button onClick={handleJoinMeeting} size="lg" className="w-full">
            {t('videoSession.actions.tryAgain', 'Try Again')}
          </Button>
        </StateCard>
      )}

      {/* Permission denied state */}
      {callState === 'permission-denied' && (
        <StateCard>
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold">
            {t('videoSession.errors.permissionDenied', 'Camera and microphone access denied')}
          </h2>
          <p className="text-muted-foreground">
            {t('videoSession.errors.permissionDeniedInstructions', 'To join the video call, please allow camera and microphone access in your browser settings, then try again.')}
          </p>
          <Button onClick={handleJoinMeeting} size="lg" className="w-full">
            {t('videoSession.actions.tryAgain', 'Try Again')}
          </Button>
        </StateCard>
      )}

      {/* Hidden audio element for remote audio playback (DOM-mounted for iOS Safari compatibility) */}
      <audio ref={audioRef} autoPlay className="hidden" />

      {/* Screen reader live region for connection status announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {statusAnnouncement}
      </div>
    </div>
  );
}
